// Shared "how this league works" content + first-visit rules pop-up.
// The copy here is the single source of truth — used both by the league
// creation intro screen (App.js) and the first-time-entering pop-up.

const FONT = `@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700&display=swap');`;

export const LEAGUE_RULES = {
  classic: {
    emoji: "🏆", name: "CLASSIC LEAGUE", accent: "#3d9e7a", bg: "#f0fdf8",
    subtitle: "The season-long credits league",
    description: "Classic is your traditional fantasy-style betting league. Everyone starts with the same amount of credits and bets on real games across your chosen sports. There are no weekly resets and no eliminations - your credits build up over the entire season. The person with the most credits at the end wins.",
    steps: [
      { icon: "💰", title: "Get your credits", desc: "Everyone starts with the same amount. Think of them as your betting bankroll for the whole season." },
      { icon: "🏈", title: "Bet on any game", desc: "Pick from spreads, moneylines, totals, or build a parlay. Bet on any game available in your chosen sports." },
      { icon: "📈", title: "Grow your stack", desc: "Every win adds to your total. Every loss takes away. Credits carry over week after week." },
      { icon: "🏆", title: "Top the leaderboard", desc: "The person with the most credits at the end of the season wins. Simple as that." },
    ],
  },
  eliminator: {
    emoji: "🎯", name: "NFL ELIMINATOR", accent: "#dc2626", bg: "#fff5f5",
    subtitle: "Last man standing wins",
    description: "NFL Eliminator is the ultimate high-stakes pick'em game. Every week you pick one NFL team to win their game. Get it right and you survive. Get it wrong and you're eliminated. The catch? You can never use the same team twice all season. One bad pick and you're done.",
    steps: [
      { icon: "🏈", title: "Pick one team per week", desc: "Choose any NFL team you think will win their game that week. Just one pick, that's it." },
      { icon: "🚫", title: "No repeats ever", desc: "Once you use a team, they're off your list forever. Choose wisely — the good teams go fast." },
      { icon: "💀", title: "One loss = eliminated", desc: "If your team loses, you're out of the league permanently. No second chances." },
      { icon: "🏆", title: "Last one standing wins", desc: "As the weeks go on, players get eliminated one by one. The last survivor wins the whole thing." },
    ],
  },
  h2h: {
    emoji: "⚔️", name: "HEAD TO HEAD", accent: "#1d4ed8", bg: "#eff6ff",
    subtitle: "Weekly matchups with playoffs",
    description: "Head to Head is the most competitive format on Scraps. Each matchup you face one other player and you both start with the same credits. You bet on games to grow your stack — whoever has more credits at the end of the matchup wins. Your record determines your playoff seeding.",
    steps: [
      { icon: "⚔️", title: "Face off 1-on-1", desc: "Each week you're paired with one opponent. You both reset to the same starting credits." },
      { icon: "🎰", title: "Bet to build your stack", desc: "Place bets throughout the week. Every win grows your credits, every loss shrinks them." },
      { icon: "📊", title: "Most credits wins the week", desc: "When time runs out, whoever has more credits wins the matchup and gets a W on their record." },
      { icon: "🏆", title: "Make the playoffs", desc: "Top records at the end of the regular season advance to a playoff bracket. Win the bracket, win the league." },
    ],
  },
};

// Full-screen overlay shown the first time a user enters a league.
// `onClose` is called once — the caller is responsible for persisting
// that the user has now seen the rules (server + local session state).
export default function LeagueRulesModal({ leagueType, onClose }) {
  const d = LEAGUE_RULES[leagueType] || LEAGUE_RULES.classic;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(13,27,42,0.75)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16, overflowY: "auto",
    }}>
      <style>{FONT}</style>
      <div style={{
        width: "100%", maxWidth: 480, background: "#ffffff", borderRadius: 20,
        padding: 32, boxShadow: "0 8px 40px rgba(0,0,0,0.4)", margin: "auto",
      }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 44, marginBottom: 8 }}>{d.emoji}</div>
          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 24, fontWeight: 800, color: "#1a1a2e" }}>{d.name}</div>
          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2, letterSpacing: "0.04em" }}>HOW THIS LEAGUE WORKS</div>
          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>{d.subtitle}</div>
        </div>

        <div style={{ background: d.bg, border: `1px solid ${d.accent}30`, borderRadius: 12, padding: "14px 16px", marginBottom: 20 }}>
          <p style={{ margin: 0, fontSize: 13, color: "#374151", lineHeight: 1.7 }}>{d.description}</p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 24 }}>
          {d.steps.map((step, i) => (
            <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: d.bg, border: `1px solid ${d.accent}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{step.icon}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1a2e", marginBottom: 2 }}>{step.title}</div>
                <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>{step.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <button onClick={onClose} style={{ width: "100%", padding: "14px", background: d.accent, color: "#fff", border: "none", borderRadius: 10, fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
          Got it — let's go
        </button>
      </div>
    </div>
  );
}
